import type { UserRole, UserStatus } from './auth-client';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3010';

class AdminApiError extends Error {
  constructor(
    message: string,
    public statusCode: number
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

async function adminFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}/admin${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    },
    credentials: 'include'
  });

  const data = await res.json();

  if (!res.ok) {
    throw new AdminApiError(data.message || 'Request failed', res.status);
  }

  return data;
}

// ============================================
// Types
// ============================================

export interface PaginatedResponse<T> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  [key: string]: T[] | PaginatedResponse<T>['pagination'];
}

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  role: UserRole;
  status: UserStatus;
  country: string;
  emailVerified: boolean;
  isAnonymized: boolean;
  anonymizedAt: string | null;
  createdAt: string;
  _count: {
    sentLetters: number;
    receivedLetters: number;
    reportsReceived: number;
  };
}

export interface AdminUserDetail {
  id: string;
  email: string;
  username: string;
  displayName: string | null;
  role: UserRole;
  status: UserStatus;
  country: string;
  locale: string;
  bio: string | null;
  emailVerified: boolean;
  isAnonymized: boolean;
  anonymizedAt: string | null;
  anonymizedBy: string | null;
  createdAt: string;
  updatedAt: string;
  _count: {
    sentLetters: number;
    receivedLetters: number;
    reportsMade: number;
    reportsReceived: number;
    sessions: number;
  };
}

export interface AdminReport {
  id: string;
  reason: string;
  details: string | null;
  status: string;
  createdAt: string;
  resolvedAt: string | null;
  adminNotes: string | null;
  reporter: { id: string; username: string; email: string };
  reported: { id: string; username: string; email: string; status: UserStatus };
  letter: { id: string; subject: string } | null;
  resolvedBy: { id: string; username: string } | null;
}

export interface AdminLetter {
  id: string;
  subject: string;
  status: string;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  isFlagged: boolean;
  flagReason: string | null;
  createdAt: string;
  sender: { id: string; username: string; isAnonymized: boolean };
  recipient: { id: string; username: string; isAnonymized: boolean };
}

export interface AdminInterest {
  id: string;
  name: string;
  category: string;
  _count: { users: number };
}

export interface AdminSession {
  id: string;
  token: string;
  expiresAt: string;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: string;
  user: { id: string; username: string; email: string; role: UserRole };
}

export interface DashboardStats {
  users: {
    totalUsers: number;
    activeUsers: number;
    suspendedUsers: number;
    bannedUsers: number;
    anonymizedUsers: number;
    adminUsers: number;
  };
  reports: {
    totalReports: number;
    pendingReports: number;
    resolvedReports: number;
    dismissedReports: number;
  };
  letters: {
    totalLetters: number;
    draftLetters: number;
    inTransitLetters: number;
    deliveredLetters: number;
    flaggedLetters: number;
  };
  sessions: {
    totalSessions: number;
    activeSessions: number;
    expiredSessions: number;
  };
}

// ============================================
// API Methods
// ============================================

export const admin = {
  // Dashboard
  getStats: () => adminFetch<DashboardStats>('/stats'),

  // Users
  users: {
    list: (params?: {
      query?: string;
      status?: UserStatus;
      role?: UserRole;
      isAnonymized?: boolean;
      page?: number;
      limit?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.set('query', params.query);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.role) searchParams.set('role', params.role);
      if (params?.isAnonymized !== undefined)
        searchParams.set('isAnonymized', String(params.isAnonymized));
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      return adminFetch<{
        users: AdminUser[];
        pagination: PaginatedResponse<AdminUser>['pagination'];
      }>(`/users${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => adminFetch<AdminUserDetail>(`/users/${id}`),
    create: (data: {
      email: string;
      username: string;
      password: string;
      country: string;
      role?: UserRole;
      emailVerified?: boolean;
    }) =>
      adminFetch<AdminUserDetail>('/users', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    updateStatus: (id: string, data: { status: UserStatus; reason?: string }) =>
      adminFetch<AdminUserDetail>(`/users/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }),
    updateRole: (id: string, data: { role: UserRole }) =>
      adminFetch<AdminUserDetail>(`/users/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }),
    anonymize: (id: string, data: { reason: string }) =>
      adminFetch<AdminUserDetail>(`/users/${id}/anonymize`, {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    forceLogout: (id: string) =>
      adminFetch<{ deletedSessions: number }>(`/users/${id}/sessions`, {
        method: 'DELETE'
      }),
    updatePassword: (id: string, data: { password: string }) =>
      adminFetch<{ success: boolean }>(`/users/${id}/password`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      })
  },

  // Reports
  reports: {
    list: (params?: {
      status?: string;
      reason?: string;
      reportedId?: string;
      page?: number;
      limit?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.reason) searchParams.set('reason', params.reason);
      if (params?.reportedId) searchParams.set('reportedId', params.reportedId);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      return adminFetch<{
        reports: AdminReport[];
        pagination: PaginatedResponse<AdminReport>['pagination'];
      }>(`/reports${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => adminFetch<AdminReport>(`/reports/${id}`),
    resolve: (id: string, data: { status: string; adminNotes?: string; takeAction?: boolean }) =>
      adminFetch<AdminReport>(`/reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      })
  },

  // Letters
  letters: {
    list: (params?: {
      senderId?: string;
      recipientId?: string;
      status?: string;
      isFlagged?: boolean;
      page?: number;
      limit?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.senderId) searchParams.set('senderId', params.senderId);
      if (params?.recipientId) searchParams.set('recipientId', params.recipientId);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.isFlagged !== undefined) searchParams.set('isFlagged', String(params.isFlagged));
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      return adminFetch<{
        letters: AdminLetter[];
        pagination: PaginatedResponse<AdminLetter>['pagination'];
      }>(`/letters${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => adminFetch<AdminLetter & { content: unknown }>(`/letters/${id}`),
    flag: (id: string, data: { reason: string; severity?: string }) =>
      adminFetch<AdminLetter>(`/letters/${id}/flag`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      }),
    unflag: (id: string) =>
      adminFetch<AdminLetter>(`/letters/${id}/unflag`, {
        method: 'PATCH'
      }),
    deliver: (id: string) =>
      adminFetch<AdminLetter>(`/letters/${id}/deliver`, {
        method: 'PATCH'
      })
  },

  // Interests
  interests: {
    list: (params?: { page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      return adminFetch<{
        interests: AdminInterest[];
        pagination: PaginatedResponse<AdminInterest>['pagination'];
      }>(`/interests${qs ? `?${qs}` : ''}`);
    },
    create: (data: { name: string; category: string }) =>
      adminFetch<AdminInterest>('/interests', {
        method: 'POST',
        body: JSON.stringify(data)
      }),
    update: (id: string, data: { name?: string; category?: string }) =>
      adminFetch<AdminInterest>(`/interests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      }),
    merge: (sourceId: string, targetId: string) =>
      adminFetch<{ message: string }>(`/interests/${sourceId}/merge`, {
        method: 'PATCH',
        body: JSON.stringify({ targetInterestId: targetId })
      }),
    delete: (id: string) =>
      adminFetch<{ message: string }>(`/interests/${id}`, {
        method: 'DELETE'
      })
  },

  // Sessions
  sessions: {
    list: (params?: { userId?: string; page?: number; limit?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.userId) searchParams.set('userId', params.userId);
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      return adminFetch<{
        sessions: AdminSession[];
        pagination: PaginatedResponse<AdminSession>['pagination'];
      }>(`/sessions${qs ? `?${qs}` : ''}`);
    },
    terminate: (id: string) =>
      adminFetch<{ message: string }>(`/sessions/${id}`, {
        method: 'DELETE'
      })
  }
};
